import React, { useState, useEffect, useRef, useCallback } from 'react';

// Simple "type the characters you see" captcha for the address form.
// 5 characters from a set without look-alikes (no 0/O, 1/I/l, 5/S, 2/Z, 8/B), drawn on a canvas with
// small rotations, wavy lines and dots. Case does not matter. ↻ gives a new one.
// Calls onChange(true) only while the typed text matches.
const CHARS = 'ACDEFGHJKLMNPQRTUVWXY34679';
const LENGTH = 5;

function makeCode() {
  let s = '';
  const buf = new Uint32Array(LENGTH);
  try { window.crypto.getRandomValues(buf); } catch { for (let i = 0; i < LENGTH; i++) buf[i] = Math.floor(Math.random() * 1e9); }
  for (let i = 0; i < LENGTH; i++) s += CHARS[buf[i] % CHARS.length];
  return s;
}

function draw(canvas, code) {
  if (!canvas) return;
  const w = 170, h = 54, ratio = window.devicePixelRatio || 1;
  canvas.width = w * ratio; canvas.height = h * ratio;
  canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
  const ctx = canvas.getContext('2d');
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  ctx.fillStyle = '#f4f1e8'; ctx.fillRect(0, 0, w, h);
  // background dots
  for (let i = 0; i < 60; i++) {
    ctx.fillStyle = `rgba(30,40,70,${0.08 + Math.random() * 0.15})`;
    ctx.beginPath(); ctx.arc(Math.random() * w, Math.random() * h, Math.random() * 1.6 + 0.4, 0, Math.PI * 2); ctx.fill();
  }
  // characters
  const step = (w - 24) / LENGTH;
  for (let i = 0; i < code.length; i++) {
    ctx.save();
    ctx.translate(16 + step * i + step / 2, h / 2 + (Math.random() * 8 - 4));
    ctx.rotate((Math.random() * 30 - 15) * Math.PI / 180);
    ctx.font = `700 ${26 + Math.floor(Math.random() * 5)}px Georgia, 'Times New Roman', serif`;
    ctx.fillStyle = ['#1f3a5f', '#3a2a1f', '#2f4f2f', '#4a1f3a'][i % 4];
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(code[i], 0, 0);
    ctx.restore();
  }
  // two wavy lines through the text
  for (let k = 0; k < 2; k++) {
    ctx.strokeStyle = `rgba(31,58,95,${0.35 + Math.random() * 0.2})`; ctx.lineWidth = 1.4;
    ctx.beginPath();
    const y0 = 14 + Math.random() * 26, amp = 4 + Math.random() * 5, ph = Math.random() * 6;
    for (let x = 0; x <= w; x += 4) { const y = y0 + Math.sin(x / 14 + ph) * amp; if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y); }
    ctx.stroke();
  }
}

export default function TextCaptcha({ onChange }) {
  const canvasRef = useRef(null);
  const [code, setCode] = useState(makeCode);
  const [value, setValue] = useState('');

  const refresh = useCallback(() => { setCode(makeCode()); setValue(''); onChange(false); }, [onChange]);

  useEffect(() => { draw(canvasRef.current, code); }, [code]);

  const handle = e => {
    const v = e.target.value.toUpperCase().replace(/\s/g, '').slice(0, LENGTH);
    setValue(v);
    onChange(v === code);
  };

  const ok = value.length === LENGTH && value === code;
  const wrong = value.length === LENGTH && value !== code;

  return (
    <div className="form-group page1-captcha">
      <label className="form-label" htmlFor="page1-captcha-input">Type the characters you see</label>
      <div className="page1-captcha-row">
        <canvas ref={canvasRef} className="page1-captcha-img" role="img" aria-label="Security check image: type the characters shown" />
        <button type="button" className="page1-captcha-refresh" onClick={refresh} title="Show different characters" aria-label="Show different characters">↻</button>
        <input
          id="page1-captcha-input"
          type="text"
          className={`form-input page1-captcha-input${ok ? ' is-ok' : ''}${wrong ? ' is-wrong' : ''}`}
          value={value}
          onChange={handle}
          maxLength={LENGTH}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder="ABC12"
        />
      </div>
      {wrong && <div className="page1-captcha-hint">That doesn't match. Try again, or tap ↻ for new characters.</div>}
    </div>
  );
}
